/*
  Cart footer / body blocks (Plan C, unit C2). Everything talks to the ONE cart controller
  (Multiverse.cart, assets/section-cart-drawer.js) and never fetches /cart/*.js itself. After a
  mutation the controller repaints the whole drawer panel (and every [data-mv-cart-section]
  mount), so these elements are re-created from fresh Liquid; anything that must outlive a repaint
  (a message to show, the ticked policies box, an open note field) is handed to the fresh element
  by id or kept in sessionStorage. Contract: docs/superpowers/plans/pC-contracts.md.

  <mv-cart-discount>  blocks/cart_discount-field.liquid — applies / removes discount codes with
                      Multiverse.cart.update({ discount: 'A,B' }) and shows the block's messages.
  <mv-cart-note>      blocks/cart_note.liquid — saves the order note on change (no repaint).
  <mv-cart-checkout>  blocks/cart_checkout-btn.liquid — spinner on submit; asks the panel's
                      <mv-cart-tnc> before letting the form go to checkout.
  <mv-cart-tnc>       blocks/cart_tnc-checkbox.liquid — the policies checkbox: state in
                      sessionStorage, warning text in the chosen slot, optional button dimming.
  <mv-cart-upsells>   blocks/cart_product-upsells.liquid — the list; moves itself to the body /
                      footer region chosen by the Position setting.
  <mv-cart-upsell>    one upsell product: variant dropdowns, price, image swap, toggle (= the
                      product is in the cart) or classic add button.
*/
(() => {
  window.Multiverse = window.Multiverse || {};
  const MV = window.Multiverse;
  const define = (name, cls) => {
    if (MV.defineOnce) MV.defineOnce(name, cls);
    else if (!customElements.get(name)) customElements.define(name, cls);
  };
  const cartApi = () => (MV.cart && typeof MV.cart.update === 'function' ? MV.cart : null);
  const panelOf = (el) => el.closest('[data-mv-cart-section]') || el.closest('cart-drawer') || document;
  const store = {
    get(key) {
      try {
        return window.sessionStorage.getItem(key);
      } catch (e) {
        return null;
      }
    },
    set(key, value) {
      try {
        window.sessionStorage.setItem(key, value);
      } catch (e) {
        /* private mode: the state just does not survive a repaint */
      }
    },
  };
  const parseJson = (el) => {
    if (!el) return null;
    try {
      return JSON.parse(el.textContent);
    } catch (e) {
      return null;
    }
  };
  const sameCode = (a, b) => String(a || '').trim().toUpperCase() === String(b || '').trim().toUpperCase();

  // Money from cents with the shop's money format (only when B6's helper is not on the page).
  const formatMoney = (cents, format) => {
    if (typeof MV.formatMoney === 'function') return MV.formatMoney(cents, format);
    const value = Math.max(0, Math.round(Number(cents) || 0));
    const pattern = format || '${{amount}}';
    const key = (/\{\{\s*(\w+)\s*\}\}/.exec(pattern) || [])[1] || 'amount';
    const noDecimals = key.includes('no_decimals');
    let thousands = ',';
    let decimal = '.';
    if (key.includes('comma_separator')) {
      thousands = '.';
      decimal = ',';
    } else if (key.includes('apostrophe')) {
      thousands = "'";
    } else if (key.includes('period_and_space')) {
      thousands = ' ';
    } else if (key.includes('space_separator')) {
      thousands = ' ';
      decimal = ',';
    }
    const fixed = (value / 100).toFixed(noDecimals ? 0 : 2).split('.');
    const int = fixed[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
    return pattern.replace(/\{\{\s*\w+\s*\}\}/, fixed[1] ? int + decimal + fixed[1] : int);
  };

  // ---- Discount field --------------------------------------------------------------------
  // Messages travel to the element that exists AFTER the repaint: `deliver` shows them on the
  // fresh block of the same id, or parks them until that block connects.
  const parkedMessages = new Map();

  class MvCartDiscount extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('[data-mv-discount-form]');
      this.input = this.querySelector('[data-mv-discount-input]');
      this.submitButton = this.querySelector('[data-mv-discount-submit]');
      if (!this._onSubmit) {
        this._onSubmit = (event) => {
          event.preventDefault();
          this.apply();
        };
        this._onClick = (event) => {
          const remove = event.target.closest('[data-mv-discount-remove]');
          if (remove && this.contains(remove)) this.removeCode(remove.dataset.mvDiscountRemove);
        };
        this._onInput = () => this.hideMessages();
      }
      if (this.form) this.form.addEventListener('submit', this._onSubmit);
      this.addEventListener('click', this._onClick);
      if (this.input) this.input.addEventListener('input', this._onInput);
      const parked = parkedMessages.get(this.id);
      if (parked) {
        parkedMessages.delete(this.id);
        this.showMessage(parked.kind, parked.value);
      }
    }

    disconnectedCallback() {
      if (this.form) this.form.removeEventListener('submit', this._onSubmit);
      this.removeEventListener('click', this._onClick);
      if (this.input) this.input.removeEventListener('input', this._onInput);
      clearTimeout(this._successTimer);
    }

    static deliver(id, kind, value) {
      const fresh = document.getElementById(id);
      if (fresh && typeof fresh.showMessage === 'function' && fresh.isConnected) fresh.showMessage(kind, value);
      else parkedMessages.set(id, { kind, value });
    }

    appliedCodes() {
      return (this.dataset.appliedCodes || '')
        .split(',')
        .map((code) => code.trim())
        .filter(Boolean);
    }

    hideMessages() {
      this.querySelectorAll('[data-mv-discount-message]').forEach((el) => (el.hidden = true));
      clearTimeout(this._successTimer);
    }

    // kind: empty | invalid | duplicate | success. `value` is the code to put back in the input.
    showMessage(kind, value) {
      this.hideMessages();
      const el = this.querySelector(`[data-mv-discount-message="${kind}"]`);
      if (el) el.hidden = false;
      if (this.input && typeof value === 'string') this.input.value = value;
      if (kind === 'success' && el) {
        const duration = parseInt(this.dataset.successDuration, 10) || 3000;
        this._successTimer = setTimeout(() => (el.hidden = true), duration);
      }
      if (kind !== 'success' && this.input) this.input.focus({ preventScroll: true });
    }

    busy(state) {
      this.classList.toggle('is-loading', !!state);
      if (this.submitButton) {
        this.submitButton.classList.toggle('is-loading', !!state);
        this.submitButton.setAttribute('aria-busy', state ? 'true' : 'false');
      }
    }

    submitCodes(codes) {
      const api = cartApi();
      if (!api) return Promise.reject(new Error('cart runtime missing'));
      return api.update({ discount: codes.join(',') }, { source: 'mv-cart-discount' });
    }

    apply() {
      const code = this.input ? this.input.value.trim() : '';
      if (!code) return this.showMessage('empty');
      const applied = this.appliedCodes();
      if (applied.some((existing) => sameCode(existing, code))) return this.showMessage('duplicate', code);
      const id = this.id;
      this.hideMessages();
      this.busy(true);
      return this.submitCodes(applied.concat(code))
        .then((cart) => {
          const entry = (cart && cart.discount_codes ? cart.discount_codes : []).find((c) => sameCode(c.code, code));
          if (entry && entry.applicable) {
            MvCartDiscount.deliver(id, 'success');
            return null;
          }
          // Shopify keeps a rejected code on the cart (applicable: false): put the previous set back.
          return this.submitCodes(applied).then(
            () => MvCartDiscount.deliver(id, 'invalid', code),
            () => MvCartDiscount.deliver(id, 'invalid', code)
          );
        })
        .catch(() => MvCartDiscount.deliver(id, 'invalid', code))
        .then(() => {
          const fresh = document.getElementById(id);
          if (fresh && typeof fresh.busy === 'function') fresh.busy(false);
        });
    }

    removeCode(code) {
      const remaining = this.appliedCodes().filter((existing) => !sameCode(existing, code));
      this.hideMessages();
      this.busy(true);
      const id = this.id;
      return this.submitCodes(remaining)
        .catch(() => MvCartDiscount.deliver(id, 'invalid'))
        .then(() => {
          const fresh = document.getElementById(id);
          if (fresh && typeof fresh.busy === 'function') fresh.busy(false);
        });
    }
  }

  // ---- Cart note ------------------------------------------------------------------------------
  class MvCartNote extends HTMLElement {
    connectedCallback() {
      this.details = this.querySelector('[data-mv-note-details]');
      this.input = this.querySelector('[data-mv-note-input]');
      this.status = this.querySelector('[data-mv-note-status]');
      this.storeKey = `mv-cart-note-open:${this.id}`;
      if (!this._onChange) {
        this._onChange = () => this.save();
        this._onToggle = () => store.set(this.storeKey, this.details.open ? '1' : '0');
      }
      if (this.details && store.get(this.storeKey) === '1') this.details.open = true;
      if (this.details) this.details.addEventListener('toggle', this._onToggle);
      if (this.input) this.input.addEventListener('change', this._onChange);
    }

    disconnectedCallback() {
      if (this.details) this.details.removeEventListener('toggle', this._onToggle);
      if (this.input) this.input.removeEventListener('change', this._onChange);
      clearTimeout(this._savedTimer);
    }

    save() {
      const api = cartApi();
      if (!api || !this.input) return;
      const note = this.input.value;
      this.dataset.state = 'saving';
      // No repaint: the customer may keep typing; the next cart change renders cart.note back.
      api
        .update({ note }, { render: false, source: 'mv-cart-note' })
        .then(() => {
          this.dataset.state = 'saved';
          clearTimeout(this._savedTimer);
          this._savedTimer = setTimeout(() => {
            if (this.dataset.state === 'saved') delete this.dataset.state;
          }, 2500);
        })
        .catch(() => {
          this.dataset.state = 'error';
        });
    }
  }

  // ---- Checkout button ---------------------------------------------------------------------
  class MvCartCheckout extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('[data-mv-checkout-form]');
      this.button = this.querySelector('[data-mv-cart-checkout]');
      if (!this._onSubmit) {
        this._onSubmit = (event) => {
          const tnc = panelOf(this).querySelector('mv-cart-tnc');
          if (tnc && typeof tnc.gate === 'function' && !tnc.gate()) {
            event.preventDefault();
            return;
          }
          this.setLoading(true);
          // Back-forward cache / a blocked navigation: release the spinner after a while.
          clearTimeout(this._resetTimer);
          this._resetTimer = setTimeout(() => this.setLoading(false), 8000);
        };
      }
      if (this.form) this.form.addEventListener('submit', this._onSubmit);
      const tnc = panelOf(this).querySelector('mv-cart-tnc');
      if (tnc && typeof tnc.applyState === 'function') tnc.applyState();
    }

    disconnectedCallback() {
      if (this.form) this.form.removeEventListener('submit', this._onSubmit);
      clearTimeout(this._resetTimer);
    }

    setLoading(state) {
      if (!this.button) return;
      this.button.classList.toggle('is-loading', !!state);
      this.button.setAttribute('aria-busy', state ? 'true' : 'false');
    }
  }

  // ---- Policies checkbox ---------------------------------------------------------------------
  class MvCartTnc extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('[data-mv-tnc-input]');
      this.blockId = this.dataset.blockId || this.id;
      this.storeKey = `mv-cart-tnc:${this.blockId}`;
      const template = this.querySelector('[data-mv-tnc-warning-text]');
      this.warningHtml = template ? template.innerHTML : '';
      if (this.input) this.input.checked = store.get(this.storeKey) === '1';
      if (!this._onChange) {
        this._onChange = () => {
          store.set(this.storeKey, this.input.checked ? '1' : '0');
          this.applyState();
        };
      }
      if (this.input) this.input.addEventListener('change', this._onChange);
      this.applyState();
    }

    disconnectedCallback() {
      if (this.input) this.input.removeEventListener('change', this._onChange);
    }

    get checked() {
      return !!(this.input && this.input.checked);
    }

    checkoutButtons() {
      return Array.from(panelOf(this).querySelectorAll('[data-mv-cart-checkout]'));
    }

    warningSlot() {
      const position = this.dataset.warningPosition || 'under-button';
      const own = this.querySelector('[data-mv-tnc-warning="under-checkbox"]');
      if (position === 'under-checkbox') return own;
      return panelOf(this).querySelector(`[data-mv-tnc-warning="${position}"]`) || own;
    }

    // Reflect the ticked state: dim the checkout button (setting) and clear the warning once ticked.
    applyState() {
      const dim = this.dataset.disableButton === 'true' && !this.checked;
      this.checkoutButtons().forEach((button) => button.classList.toggle('mv-cart-checkout__button--disabled', dim));
      this.dataset.checked = this.checked ? 'true' : 'false';
      if (this.checked) this.hideWarning();
    }

    hideWarning() {
      panelOf(this)
        .querySelectorAll(`[data-mv-tnc-warning][data-mv-tnc-for="${this.blockId}"]`)
        .forEach((slot) => {
          slot.hidden = true;
          slot.innerHTML = '';
          delete slot.dataset.mvTncFor;
        });
    }

    showWarning() {
      const slot = this.warningSlot();
      if (!slot) return;
      slot.innerHTML = this.warningHtml;
      slot.dataset.mvTncFor = this.blockId;
      slot.hidden = false;
    }

    // Called by the checkout block: true = go ahead.
    gate() {
      if (this.checked) return true;
      this.showWarning();
      if (this.input) this.input.focus({ preventScroll: true });
      return false;
    }
  }

  // ---- Upsells ------------------------------------------------------------------------------
  class MvCartUpsells extends HTMLElement {
    connectedCallback() {
      // Position setting: a block dropped in the other region moves itself (once per repaint).
      const wanted = this.dataset.position;
      const body = this.closest('[data-mv-cart-body]');
      const footer = this.closest('[data-mv-cart-footer]');
      if (wanted === 'footer' && body) {
        const target = body.parentElement && body.parentElement.querySelector('[data-mv-cart-footer]');
        if (target && !target.contains(this)) target.prepend(this);
      } else if (wanted === 'body' && footer) {
        const target = footer.parentElement && footer.parentElement.querySelector('[data-mv-cart-body]');
        if (target && !target.contains(this)) target.append(this);
      }
    }
  }

  class MvCartUpsell extends HTMLElement {
    connectedCallback() {
      this.isToggle = this.dataset.toggle === 'true';
      this.skipUnavailable = this.dataset.skipUnavailable === 'true';
      this.updatePrices = this.dataset.updatePrices === 'true';
      this.percentageLeft = parseFloat(this.dataset.percentageLeft);
      if (Number.isNaN(this.percentageLeft)) this.percentageLeft = 1;
      this.fixedDiscount = parseFloat(this.dataset.fixedDiscount) || 0;
      this.variants = parseJson(this.querySelector('script[data-mv-upsell-variants]')) || [];
      this.selects = Array.from(this.querySelectorAll('select[data-mv-upsell-option]'));
      this.variant = this.variants.find((v) => String(v.id) === this.dataset.variantId) || null;
      this.unavailableLabel = this.dataset.unavailableLabel || '[value]';
      this.errorSlot = this.querySelector('[data-mv-upsell-error]');
      this.busy = false;

      if (!this._onToggleClick) {
        this._onToggleClick = (event) => {
          if (event.target.closest('select, a, [data-mv-upsell-add]')) return;
          event.preventDefault();
          this.toggle();
        };
        this._onSelectChange = (event) => this.onOptionChange(event.target);
        this._onAddClick = (event) => {
          event.preventDefault();
          this.addClassic();
        };
      }
      // "Whole container" mode: the container is the only click target (the button's clicks
      // bubble to it), so one handler runs per click.
      const container = this.classList.contains('mv-upsell--toggle-container') ? this.querySelector('.mv-upsell__container') : null;
      this.clickTargets = container ? [container] : Array.from(this.querySelectorAll('[data-mv-upsell-toggle]'));
      this.clickTargets.forEach((el) => el.addEventListener('click', this._onToggleClick));
      this.selects.forEach((select) => select.addEventListener('change', this._onSelectChange));
      this.addButton = this.querySelector('[data-mv-upsell-add]');
      if (this.addButton) this.addButton.addEventListener('click', this._onAddClick);

      this.refreshOptionStates();
      this.maybePreselect();
    }

    disconnectedCallback() {
      (this.clickTargets || []).forEach((el) => el.removeEventListener('click', this._onToggleClick));
      (this.selects || []).forEach((select) => select.removeEventListener('change', this._onSelectChange));
      if (this.addButton) this.addButton.removeEventListener('click', this._onAddClick);
      this.clickTargets = [];
    }

    get selected() {
      return this.isToggle && this.dataset.selected === 'true';
    }

    setBusy(state) {
      this.busy = !!state;
      this.classList.toggle('is-loading', this.busy);
      this.setAttribute('aria-busy', this.busy ? 'true' : 'false');
      if (this.addButton) this.addButton.classList.toggle('is-loading', this.busy);
    }

    showError(message) {
      if (!this.errorSlot) return;
      this.errorSlot.textContent = message || '';
      this.errorSlot.hidden = !message;
    }

    paintSelected(next) {
      this.dataset.selected = next ? 'true' : 'false';
      this.querySelectorAll('[data-mv-upsell-toggle]').forEach((btn) => btn.setAttribute('aria-checked', String(next)));
    }

    // The cart line that stands for this product (any variant).
    lineKey() {
      if (this.dataset.lineKey) return this.dataset.lineKey;
      const api = cartApi();
      const state = api && typeof api.getState === 'function' ? api.getState() : null;
      const line = state && state.items ? state.items.find((item) => String(item.product_id) === this.dataset.productId) : null;
      return line ? line.key : '';
    }

    // ---- toggle = add / remove the product ----
    toggle() {
      if (!this.isToggle || this.busy) return;
      const api = cartApi();
      if (!api) return;
      const next = !this.selected;
      this.showError('');
      this.paintSelected(next);
      this.setBusy(true);
      let request;
      if (next) {
        if (!this.variant) return this.setBusy(false);
        request = api.add([{ id: this.variant.id, quantity: 1 }], { source: 'mv-cart-upsells', open: false });
      } else {
        const key = this.lineKey();
        request = key ? api.change({ id: key, quantity: 0 }, undefined, { source: 'mv-cart-upsells' }) : Promise.resolve();
      }
      request
        .then(() => {
          this.dispatchEvent(new CustomEvent('mv:cart-upsell-toggle', { bubbles: true, detail: { selected: next, variantId: this.variant ? this.variant.id : null } }));
        })
        .catch((error) => {
          this.paintSelected(!next);
          this.showError(error && error.message);
        })
        .then(() => this.setBusy(false));
    }

    addClassic() {
      if (this.busy || !this.variant) return;
      const api = cartApi();
      if (!api) return;
      this.showError('');
      this.setBusy(true);
      api
        .add([{ id: this.variant.id, quantity: 1 }], { source: 'mv-cart-upsells', open: false })
        .catch((error) => this.showError(error && error.message))
        .then(() => this.setBusy(false));
    }

    // "Preselected": add the product once per session, the first time the block shows with a
    // non-empty cart (a preselected upsell rides along with what the customer already picked).
    maybePreselect() {
      if (!this.isToggle || this.selected || this.dataset.preselected !== 'true' || !this.variant) return;
      const key = `mv-cart-upsell-preselected:${this.id}`;
      if (store.get(key) === '1') return;
      const api = cartApi();
      const state = api && typeof api.getState === 'function' ? api.getState() : null;
      if (!state || !state.item_count) return;
      store.set(key, '1');
      this.toggle();
    }

    // ---- variants ----
    selectedValues() {
      return this.selects.map((select) => select.value);
    }

    findVariant(values) {
      return this.variants.find((v) => values.every((value, i) => v.options[i] === value)) || null;
    }

    // Closest variant keeping the value the customer just picked (prefers available ones when
    // sold-out variants are skipped, then the one sharing the most other values).
    bestMatch(values, position) {
      const candidates = this.variants.filter((v) => v.options[position] === values[position]);
      if (!candidates.length) return null;
      const pool = this.skipUnavailable && candidates.some((v) => v.available) ? candidates.filter((v) => v.available) : candidates;
      const score = (v) => v.options.reduce((n, value, i) => n + (value === values[i] ? 1 : 0), 0);
      return pool.slice().sort((a, b) => score(b) - score(a))[0];
    }

    onOptionChange(select) {
      const position = this.selects.indexOf(select);
      const values = this.selectedValues();
      let variant = this.findVariant(values);
      if (!variant || (this.skipUnavailable && !variant.available)) {
        variant = this.bestMatch(values, position) || variant;
        if (variant) this.selects.forEach((sel, i) => (sel.value = variant.options[i]));
      }
      const previous = this.variant;
      this.setVariant(variant);
      this.refreshOptionStates();
      if (this.selected && variant && previous && variant.id !== previous.id) this.swapLine(variant);
    }

    // The toggle is on and the variant changed: replace the cart line with the new variant.
    swapLine(variant) {
      const api = cartApi();
      const key = this.lineKey();
      if (!api || !key || this.busy) return;
      this.showError('');
      this.setBusy(true);
      api
        .change({ id: key, quantity: 0 }, undefined, { source: 'mv-cart-upsells', render: false })
        .then(() => api.add([{ id: variant.id, quantity: 1 }], { source: 'mv-cart-upsells', open: false }))
        .catch((error) => this.showError(error && error.message))
        .then(() => this.setBusy(false));
    }

    // Option N's values are judged against the variants matching the chosen values of options
    // 1..N-1: values that do not exist are hidden, sold-out ones labelled (or hidden when skipped).
    refreshOptionStates() {
      const values = this.selectedValues();
      this.selects.forEach((select, position) => {
        Array.from(select.options).forEach((option) => {
          const matching = this.variants.filter(
            (v) => v.options[position] === option.value && v.options.slice(0, position).every((value, i) => value === values[i])
          );
          const exists = matching.length > 0;
          const available = matching.some((v) => v.available);
          const hidden = !exists || (!available && this.skipUnavailable);
          option.disabled = hidden;
          option.hidden = hidden;
          option.textContent = exists && !available ? this.unavailableLabel.replace('[value]', option.value) : option.value;
        });
      });
    }

    setVariant(variant) {
      this.variant = variant;
      this.dataset.variantId = variant ? String(variant.id) : '';
      if (this.addButton) {
        const unavailable = !variant || !variant.available;
        this.addButton.disabled = unavailable;
        const soldOut = this.addButton.querySelector('.sold-out-message');
        const label = this.addButton.querySelector('.mv-cart-upsell__add-label');
        if (soldOut) soldOut.classList.toggle('hidden', !unavailable);
        if (label) label.classList.toggle('hidden', unavailable);
      }
      if (this.updatePrices && variant) this.renderPrice(variant);
      if (variant && variant.featured_image && this.dataset.customImage !== 'true') {
        const img = this.querySelector('[data-mv-upsell-image]');
        if (img) {
          img.removeAttribute('srcset');
          img.src = variant.featured_image.src;
        }
      }
    }

    renderPrice(variant) {
      const format = this.dataset.moneyFormat;
      const price = Math.max(0, Math.round(variant.price * this.percentageLeft - this.fixedDiscount));
      const compare = variant.compare_at_price && variant.compare_at_price > variant.price ? variant.compare_at_price : variant.price;
      this.querySelectorAll('[data-mv-upsell-price-current]').forEach((el) => (el.textContent = formatMoney(price, format)));
      this.querySelectorAll('[data-mv-upsell-price-compare]').forEach((el) => {
        el.textContent = formatMoney(compare, format);
        el.classList.toggle('hidden', !(compare > price));
      });
    }
  }

  define('mv-cart-discount', MvCartDiscount);
  define('mv-cart-note', MvCartNote);
  define('mv-cart-checkout', MvCartCheckout);
  define('mv-cart-tnc', MvCartTnc);
  define('mv-cart-upsells', MvCartUpsells);
  define('mv-cart-upsell', MvCartUpsell);
})();
